import { useNotificationContext } from '@modules/common/components/Notification/NotificationContext';
import useIsElementInScreenView from '@modules/common/hooks/useIsElementInScreenView';
import { useGetFeedbackList } from '@modules/feedback/hooks/useGetFeedbackList';
import { useGetFeedbacksFromResponse } from '@modules/feedback/hooks/useGetFeedbacksFromResponse';
import { GetRecordsResponseViewFragment } from 'generated/graphql';
import { useState } from 'react';
import { useDeepCompareEffect } from 'react-use';
import { Feedback } from 'v2/core/entities/feedback';

interface UseFeedbackListInfiniteScrollOptions {
  feedbacks: Feedback[];
  totalRecords: number;
  fetchMore: ReturnType<typeof useGetFeedbackList>['fetchMore'];
  setFeedbacks: (feedbacks: Feedback[]) => void;
  isDisabled?: boolean;
}

export const useFeedbackListInfiniteScroll = ({
  feedbacks,
  totalRecords,
  fetchMore,
  setFeedbacks,
  isDisabled = false,
}: UseFeedbackListInfiniteScrollOptions) => {
  const { showErrorNotification } = useNotificationContext();

  const [feedbackRecords, setFeedbackRecords] =
    useState<GetRecordsResponseViewFragment>();

  const { feedbackRecords: updatedFeedbackRecords } =
    useGetFeedbacksFromResponse(feedbackRecords);

  useDeepCompareEffect(() => {
    if (!updatedFeedbackRecords || !feedbacks) {
      return;
    }
    setFeedbacks([...feedbacks, ...updatedFeedbackRecords]);
  }, [updatedFeedbackRecords]);

  const canFetchMore = feedbacks.length < totalRecords;

  const [setTriggerPointInViewRef] = useIsElementInScreenView({
    threshold: 0.1,
    triggerOnce: true,
    onChange: async (inView) => {
      if (!inView || isDisabled) {
        return;
      }

      if (!canFetchMore) {
        return;
      }

      try {
        const resp = await fetchMore();

        const fetchedRecords = resp?.data?.executeEQL
          ?.result as GetRecordsResponseViewFragment;

        setFeedbackRecords(fetchedRecords);
      } catch (e) {
        showErrorNotification({
          props: {},
          contentComponent: 'Failed to fetch feedback records',
        });
      }
    },
  });

  return setTriggerPointInViewRef;
};
